import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Toggle } from "@/components/ui/toggle";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { 
  Globe, 
  Linkedin, 
  Instagram, 
  Facebook, 
  Twitter,
  Code,
  Mail,
  BarChart,
  ShoppingCart,
  Layout,
  Database,
  GitBranch,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Share2,
  
  Sparkles
} from 'lucide-react';
import { SiWordpress, SiMailchimp, SiSalesforce, SiWix, SiContentful } from 'react-icons/si';
import { TikTokLogo } from '@/components/icons/TikTokLogo';
import { XLogo } from '@/components/icons/XLogo';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

interface IntegrationOption {
  id: string;
  label: string;
  icon: JSX.Element;
  description?: string;
}

interface IntegrationGroup {
  id: string;
  label: string;
  description: string;
  options: IntegrationOption[];
}

interface IntegrationSelectionProps {
  selectedIntegrations: { [key: string]: string[] };
  onIntegrationToggle: (groupId: string, optionId: string) => void;
}

const INTEGRATION_GROUPS: IntegrationGroup[] = [
  {
    id: 'some',
    label: 'Social Media',
    description: 'Choose which social media platforms you use',
    options: [
      { id: 'linkedin', label: 'LinkedIn', icon: <Linkedin className="h-5 w-5" /> },
      { id: 'instagram', label: 'Instagram', icon: <Instagram className="h-5 w-5" /> },
      { id: 'tiktok', label: 'TikTok', icon: <TikTokLogo className="h-5 w-5" /> },
      { id: 'facebook', label: 'Facebook', icon: <Facebook className="h-5 w-5" /> },
      { id: 'x', label: 'X (Twitter)', icon: <XLogo className="h-5 w-5" /> },
    ]
  },
  {
    id: 'cms',
    label: 'Content management',
    description: 'Select where you want to publish your content',
    options: [
      { id: 'wordpress', label: 'WordPress', icon: <SiWordpress className="h-5 w-5" /> },
      { id: 'shopify', label: 'Shopify', icon: <ShoppingCart className="h-5 w-5" /> },
      { id: 'wix', label: 'Wix', icon: <SiWix className="h-5 w-5" /> },
      { id: 'contentful', label: 'Contentful', icon: <SiContentful className="h-5 w-5" /> },
      { id: 'api', label: 'API', icon: <Code className="h-5 w-5" /> },
      
      { id: 'other_cms', label: 'Other CMS', icon: <Globe className="h-5 w-5" /> },
    ]
  },
  {
    id: 'crm',
    label: 'Email & CRM',
    description: 'Select your email marketing or CRM platform',
    options: [
      { id: 'mailchimp', label: 'Mailchimp', icon: <SiMailchimp className="h-5 w-5" /> },
      { id: 'activecampaign', label: 'ActiveCampaign', icon: <Mail className="h-5 w-5" /> },
      { id: 'hubspot', label: 'HubSpot', icon: <Mail className="h-5 w-5" /> },
      { id: 'salesforce', label: 'Salesforce', icon: <SiSalesforce className="h-5 w-5" /> },
      { id: 'other_crm', label: 'Other CRM', icon: <Mail className="h-5 w-5" /> },
    ]
  },
  {
    id: 'analytics',
    label: 'Analytics',
    description: 'Connect your analytics tools',
    options: [
      { id: 'google_search_console', label: 'Google Search Console', icon: <BarChart className="h-5 w-5" /> },
      { id: 'other_analytics', label: 'Other Analytics', icon: <BarChart className="h-5 w-5" /> },
    ]
  }
];

export const IntegrationSelection: React.FC<IntegrationSelectionProps> = ({
  selectedIntegrations,
  onIntegrationToggle
}) => {
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const currentGroup = INTEGRATION_GROUPS[currentGroupIndex];

  // Add useEffect to load saved selections
  useEffect(() => {
    const savedIntegrations = localStorage.getItem('onboarding_integrations');
    if (savedIntegrations) {
      try {
        const parsed = JSON.parse(savedIntegrations);
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
          // Only update if the saved integrations are different from current state
          const currentKeys = Object.keys(selectedIntegrations);
          const savedKeys = Object.keys(parsed);
          
          if (currentKeys.length !== savedKeys.length || 
              currentKeys.some(key => !savedKeys.includes(key)) ||
              savedKeys.some(key => !currentKeys.includes(key)) ||
              currentKeys.some(key => {
                const currentOptions = selectedIntegrations[key] || [];
                const savedOptions = parsed[key] || [];
                return currentOptions.length !== savedOptions.length ||
                       currentOptions.some(opt => !savedOptions.includes(opt)) ||
                       savedOptions.some(opt => !currentOptions.includes(opt));
              })) {
            // Call onIntegrationToggle for each saved integration to update parent state
            Object.entries(parsed).forEach(([groupId, options]) => {
              if (Array.isArray(options)) {
                options.forEach(optionId => {
                  onIntegrationToggle(groupId, optionId);
                });
              }
            });
          }
        }
      } catch (error) {
        console.error('Error loading saved integrations:', error);
      }
    }
  }, [window.location.search]); // Re-run when URL changes

  const handleNext = () => {
    if (currentGroupIndex < INTEGRATION_GROUPS.length - 1) {
      setCurrentGroupIndex(prev => prev + 1);
    } else {
      // Store selected integrations in localStorage
      localStorage.setItem('onboarding_integrations', JSON.stringify(selectedIntegrations));
      
      // Set completion state
      setIsComplete(true);
      
      // Show success message immediately
      toast.success('Integration preferences saved successfully!');
      
      // Call the toggle with success to indicate completion
      onIntegrationToggle('success', 'complete');
    }
  };

  const handlePrevious = () => {
    if (currentGroupIndex > 0) {
      setCurrentGroupIndex(prev => prev - 1);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {currentGroup.id === 'some' && <Share2 className="h-5 w-5 text-primary" />}
              {currentGroup.id === 'cms' && <Layout className="h-5 w-5 text-primary" />}
              {currentGroup.id === 'crm' && <Mail className="h-5 w-5 text-primary" />}
              {currentGroup.id === 'analytics' && <BarChart className="h-5 w-5 text-primary" />}
              {currentGroup.label}
            </CardTitle>
            <CardDescription>
              {isComplete ? (
                <span className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  Great! We now know what we need to know.
                </span>
              ) : (
                currentGroup.description
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {currentGroupIndex + 1} of {INTEGRATION_GROUPS.length}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!isComplete ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {currentGroup.options.map((option) => {
                const isSelected = selectedIntegrations[currentGroup.id]?.includes(option.id);
                return (
                  <div key={option.id} className="flex flex-col items-center gap-2">
                    <Toggle
                      pressed={isSelected}
                      onPressedChange={() => onIntegrationToggle(currentGroup.id, option.id)}
                      className="w-full h-auto p-4 flex flex-col items-center gap-2"
                    >
                      <div className="text-muted-foreground">
                        {option.icon}
                      </div>
                      <Label className="text-sm font-medium">{option.label}</Label>
                    </Toggle>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentGroupIndex === 0}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                onClick={handleNext}
                className="gap-2"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          <div className="flex justify-center items-center py-8">
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <p className="text-lg font-medium">All set!</p>
              <p className="text-muted-foreground">Your integration preferences have been saved.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}; 